import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Dropdown, Menu, Row } from 'antd';
import {
    TbBrowser,
    TbCheck,
    TbCube,
    TbDevices,
    TbDotsVertical,
    TbPyramid,
    TbRotate3D,
    TbScanEye,
    TbUserCancel,
    TbUserCircle,
    TbUserMinus,
} from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { WorkspaceResourceFilterSetter } from '@/hooks/useWorkspaceResourceFilters';
import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import { memberService } from '@/services';
import {
    ActivityActorIdentity,
    ActivityCodeDisclosure,
    ActivityTimelineItem,
    DropdownMenuPanel,
    VirtualizedList as List,
    MiddleDot,
    SmallTooltip,
    useVirtualizedListItemRemeasure,
} from '@/shared';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores';
import { AuditLog, ListViewColumnDataType, Member, MemberPermissionType, MemberStatusType } from '@/types';
import { dayjs, formatDate } from '@/utilities';

import AuditLogActionIcon from '../AuditLogActionIcon';
import {
    getAuditActorName,
    getAuditPermission,
    normalizeAuditAction,
    normalizeAuditResource,
} from '../auditLogFilters';
import { getAuditActionSummary, getAuditLogDetailPayload } from '../auditLogPresentation';
import AuditLogResourceIdentity from '../AuditLogResourceIdentity';
import DangerModal from './DangerModal';
import MemberPermissionModal from './MemberPermissionModal';

interface AuditLogListItemProps {
    filters: ListFilterRule[];
    isLast?: boolean;
    log: AuditLog;
    nestingDepth?: 0 | 1;
    nextNestingDepth?: 0 | 1;
    onFiltersChange: WorkspaceResourceFilterSetter;
    onMemberUpdated: (member: Member) => void;
}

interface MetadataFilterItem {
    field: string;
    fieldLabel: string;
    icon: JSX.Element;
    key: string;
    label: string;
    value: string;
}

const AuditLogListItem: FC<AuditLogListItemProps> = ({
    filters,
    isLast = false,
    log,
    nestingDepth = 0,
    nextNestingDepth = 0,
    onFiltersChange,
    onMemberUpdated,
}): JSX.Element => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [permissionOpen, setPermissionOpen] = useState(false);
    const [dangerAction, setDangerAction] = useState<'block' | 'deactivate'>('deactivate');
    const [dangerOpen, setDangerOpen] = useState(false);
    const [member, setMember] = useState<Member | undefined>(log.member ?? log.user?.activeMember);
    const itemContentRef = useRef<HTMLDivElement>(null);
    const previousDetailsOpenRef = useRef(detailsOpen);
    const remeasureListItem = useVirtualizedListItemRemeasure();
    const { selectedUser } = useUserStore();
    const { token } = useAntDesignTheme();

    const actionLabel = normalizeAuditAction(log.action);
    const resourceLabel = normalizeAuditResource(log.entityType);
    const displayLog = useMemo(() => ({ ...log, member: member ?? log.member }), [log, member]);
    const canManageMember = Boolean(
        selectedUser?.activeMember?.permission === MemberPermissionType.OWNER &&
        member?.id &&
        member.id !== selectedUser.activeMember.id &&
        member.permission !== MemberPermissionType.OWNER,
    );

    useLayoutEffect(() => {
        if (previousDetailsOpenRef.current === detailsOpen) {
            return;
        }

        previousDetailsOpenRef.current = detailsOpen;
        remeasureListItem(itemContentRef.current);
    }, [detailsOpen, remeasureListItem]);

    const handleMemberUpdated = (updatedMember: Member) => {
        setMember(updatedMember);
        onMemberUpdated(updatedMember);
    };

    const metadataFilterItems: MetadataFilterItem[] = [
        {
            field: 'actor',
            fieldLabel: 'Actor',
            icon: <TbUserCircle size='1rem' />,
            key: 'account',
            label: 'Account',
            value: getAuditActorName(displayLog),
        },
        {
            field: 'permission',
            fieldLabel: 'Permission',
            icon: <TbPyramid size='1rem' />,
            key: 'permission',
            label: 'Permission',
            value: getAuditPermission(displayLog),
        },
        {
            field: 'action',
            fieldLabel: 'Action',
            icon: <TbRotate3D size='1rem' />,
            key: 'action',
            label: 'Action',
            value: actionLabel,
        },
        {
            field: 'resource',
            fieldLabel: 'Resource',
            icon: <TbCube size='1rem' />,
            key: 'resource',
            label: 'Resource',
            value: resourceLabel,
        },
        {
            field: 'deviceType',
            fieldLabel: 'Device Type',
            icon: <TbDevices size='1rem' />,
            key: 'deviceType',
            label: 'Device Type',
            value: log.deviceType ?? 'Unknown',
        },
        {
            field: 'browser',
            fieldLabel: 'Browser',
            icon: <TbBrowser size='1rem' />,
            key: 'browser',
            label: 'Browser',
            value: log.browser ?? 'Unknown',
        },
    ];

    const isMetadataFilterActive = (item: MetadataFilterItem) =>
        filters.some(
            (filter) =>
                filter.field === item.field &&
                filter.operator === 'equals' &&
                (filter.value === item.value ||
                    (Array.isArray(filter.value) && filter.value.some((value) => value === item.value))),
        );

    const handleMetadataFilter = (item: MetadataFilterItem) => {
        const otherFilters = filters.filter((filter) => filter.field !== item.field);

        if (isMetadataFilterActive(item)) {
            onFiltersChange(otherFilters);
            return;
        }

        onFiltersChange([
            ...otherFilters,
            {
                dataType: ListViewColumnDataType.SELECT,
                field: item.field,
                fieldLabel: item.fieldLabel,
                id: `audit-log-${item.field}-${item.key}`,
                operator: 'equals',
                operatorLabel: 'Equals',
                value: item.value,
            },
        ]);
    };

    const handleReactivate = async () => {
        if (!member?.id) {
            return;
        }

        try {
            const updatedMember = await memberService.update(member.id, { status: MemberStatusType.ACTIVE });
            handleMemberUpdated(updatedMember);
            toast('Member Reactivated', {
                description: `${member.user?.firstName} ${member.user?.lastName}`,
            });
        } catch (error) {
            captureException(error);
            toast.error('Unable to Reactivate Member');
        }
    };

    return (
        <>
            <List.Item style={{ display: 'block', padding: '0.375rem 0.5rem 0', width: '100%' }}>
                <div ref={itemContentRef} style={{ minWidth: 0, width: '100%' }}>
                    <ActivityTimelineItem
                        action={
                            <Dropdown
                                trigger={['click']}
                                placement='bottomRight'
                                overlayStyle={{ minWidth: 300 }}
                                popupRender={() => (
                                    <DropdownMenuPanel>
                                        <small
                                            style={{
                                                marginLeft: '0.75rem',
                                                color: token.colorTextTertiary,
                                                verticalAlign: '0.2rem',
                                            }}
                                        >
                                            Filter By Metadata
                                        </small>
                                        {metadataFilterItems.map((item) => {
                                            const isActive = isMetadataFilterActive(item);

                                            return (
                                                <Menu.Item
                                                    key={item.key}
                                                    icon={item.icon}
                                                    onClick={() => handleMetadataFilter(item)}
                                                >
                                                    <Row align='middle' wrap={false}>
                                                        <Col flex='auto'>{item.label}</Col>
                                                        <Col
                                                            flex='none'
                                                            style={{
                                                                color: isActive
                                                                    ? token.colorPrimary
                                                                    : token.colorTextTertiary,
                                                                marginLeft: '1rem',
                                                                textAlign: 'right',
                                                            }}
                                                        >
                                                            <small>{item.value}</small>
                                                            {isActive && (
                                                                <TbCheck
                                                                    size='0.875rem'
                                                                    style={{
                                                                        marginLeft: '0.375rem',
                                                                        verticalAlign: '-0.15rem',
                                                                    }}
                                                                />
                                                            )}
                                                        </Col>
                                                    </Row>
                                                </Menu.Item>
                                            );
                                        })}
                                        {canManageMember && (
                                            <>
                                                <Menu.Divider />
                                                <Menu.Item
                                                    key='changeUserPermission'
                                                    disabled={member?.status !== MemberStatusType.ACTIVE}
                                                    icon={<TbScanEye size='1rem' />}
                                                    onClick={() => setPermissionOpen(true)}
                                                >
                                                    Change User's Permission
                                                </Menu.Item>
                                                <Menu.Divider />
                                                {member?.status === MemberStatusType.ACTIVE ? (
                                                    <Menu.Item
                                                        key='deactivate'
                                                        danger
                                                        icon={<TbUserMinus size='1rem' />}
                                                        onClick={() => {
                                                            setDangerAction('deactivate');
                                                            setDangerOpen(true);
                                                        }}
                                                    >
                                                        Deactivate User
                                                    </Menu.Item>
                                                ) : (
                                                    <Menu.Item key='reactivate' onClick={handleReactivate}>
                                                        Reactivate User
                                                    </Menu.Item>
                                                )}
                                                {member?.status !== MemberStatusType.BLOCKED && (
                                                    <Menu.Item
                                                        key='block'
                                                        danger
                                                        icon={<TbUserCancel size='1rem' />}
                                                        onClick={() => {
                                                            setDangerAction('block');
                                                            setDangerOpen(true);
                                                        }}
                                                    >
                                                        Block User
                                                    </Menu.Item>
                                                )}
                                            </>
                                        )}
                                    </DropdownMenuPanel>
                                )}
                            >
                                <Button
                                    aria-label={`Actions for ${getAuditActorName(displayLog)} ${actionLabel.toLocaleLowerCase()} ${resourceLabel}`}
                                    size='small'
                                    type='text'
                                    icon={
                                        <TbDotsVertical
                                            style={{ verticalAlign: '-0.15rem', color: token.colorTextTertiary }}
                                        />
                                    }
                                />
                            </Dropdown>
                        }
                        density='compact'
                        icon={<AuditLogActionIcon log={displayLog} />}
                        isLast={isLast}
                        nestingDepth={nestingDepth}
                        nextNestingDepth={nextNestingDepth}
                        metadata={
                            <>
                                <Row align='middle' style={{ minWidth: 0, width: '100%' }} wrap={false}>
                                    <Col flex='auto' style={{ minWidth: 0 }}>
                                        <small style={{ color: token.colorTextTertiary }}>
                                            <SmallTooltip
                                                placement='topLeft'
                                                title={
                                                    member?.createdAt
                                                        ? `Joined ${dayjs(member.createdAt).format('MMMM Do YYYY, h:mm:ss A')}`
                                                        : 'Membership date unavailable'
                                                }
                                            >
                                                {member?.permission ?? 'Unknown'}
                                            </SmallTooltip>
                                            <MiddleDot horizontalMargin='0.5rem' />
                                            <SmallTooltip placement='topLeft' title={log.ipAddress || 'No IP Address'}>
                                                {log.browser || 'Browser'} ({log.deviceType || 'Desktop'})
                                            </SmallTooltip>
                                            {log.createdAt && (
                                                <>
                                                    <MiddleDot horizontalMargin='0.5rem' />
                                                    <SmallTooltip
                                                        placement='topLeft'
                                                        title={dayjs(log.createdAt).format('MMMM Do YYYY, h:mm:ss A')}
                                                    >
                                                        {formatDate(log.createdAt)}
                                                    </SmallTooltip>
                                                </>
                                            )}
                                        </small>
                                    </Col>
                                </Row>
                                <ActivityCodeDisclosure
                                    code={JSON.stringify(getAuditLogDetailPayload(displayLog), null, 2)}
                                    expanded={detailsOpen}
                                    id={`audit-log-details-${log.id}`}
                                    label='Log Details'
                                    onExpandedChange={setDetailsOpen}
                                />
                            </>
                        }
                        title={
                            <span
                                style={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '0.25rem',
                                    minWidth: 0,
                                }}
                            >
                                <ActivityActorIdentity member={member ?? log.member} user={log.user} />
                                <span style={{ color: token.colorTextSecondary }}>
                                    {getAuditActionSummary(displayLog)}
                                </span>
                                <AuditLogResourceIdentity log={displayLog} />
                            </span>
                        }
                    />
                </div>
            </List.Item>
            <MemberPermissionModal
                member={member}
                onUpdated={handleMemberUpdated}
                open={permissionOpen}
                setOpen={setPermissionOpen}
            />
            <DangerModal
                member={member}
                onUpdated={handleMemberUpdated}
                open={dangerOpen}
                setOpen={setDangerOpen}
                value={dangerAction}
            />
        </>
    );
};

export default AuditLogListItem;
